import {useEffect} from 'react';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {v4 as uuid} from 'uuid';

import type {Annotation, AnnotationRange, AnnotationType, HighlightColor} from './annotationTypes';

export interface AnnotationModeState {
  isAnnotationModeOn: boolean;
  annotationsByQuestionId: Record<string, Annotation[]>;
  defaultColor: HighlightColor;
  defaultType: AnnotationType;
  hasAnnotatableContent: boolean;
}

interface AnnotationModeActions {
  toggleAnnotationMode: () => void;
  addAnnotation: (questionId: string, range: AnnotationRange) => string;
  setHighlightColor: (questionId: string, id: string, color: HighlightColor) => void;
  setAnnotationUnderline: (questionId: string, id: string) => void;
  removeAnnotation: (questionId: string, id: string) => void;
  setHasAnnotatableContent: (hasContent: boolean) => void;
  reset: () => void;
}

const initialState: AnnotationModeState = {
  isAnnotationModeOn: false,
  annotationsByQuestionId: {},
  defaultColor: 'yellow',
  defaultType: 'highlight',
  hasAnnotatableContent: false
};

export const useAnnotationModeStore = create<AnnotationModeState & AnnotationModeActions>()(
  devtools(
    (set) => ({
      ...initialState,

      toggleAnnotationMode: () => {
        set(
          (state) => ({isAnnotationModeOn: !state.isAnnotationModeOn}),
          false,
          'toggleAnnotationMode'
        );
      },

      addAnnotation: (questionId: string, range: AnnotationRange) => {
        const id = uuid();
        set(
          (state) => ({
            annotationsByQuestionId: {
              ...state.annotationsByQuestionId,
              [questionId]: [
                ...(state.annotationsByQuestionId[questionId] ?? []),
                {id, ...range, color: state.defaultColor, type: state.defaultType}
              ]
            }
          }),
          false,
          'addAnnotation'
        );
        return id;
      },

      // Recolor one annotation as a highlight and make that color/type the default for new ones.
      setHighlightColor: (questionId: string, id: string, color: HighlightColor) => {
        set(
          (state) => ({
            defaultColor: color,
            defaultType: 'highlight',
            annotationsByQuestionId: {
              ...state.annotationsByQuestionId,
              [questionId]: (state.annotationsByQuestionId[questionId] ?? []).map((annotation) =>
                annotation.id === id ? {...annotation, color, type: 'highlight'} : annotation
              )
            }
          }),
          false,
          'setHighlightColor'
        );
      },

      // Switch one annotation to underline (mutually exclusive with color) and make it the default.
      setAnnotationUnderline: (questionId: string, id: string) => {
        set(
          (state) => ({
            defaultType: 'underline',
            annotationsByQuestionId: {
              ...state.annotationsByQuestionId,
              [questionId]: (state.annotationsByQuestionId[questionId] ?? []).map((annotation) =>
                annotation.id === id ? {...annotation, type: 'underline'} : annotation
              )
            }
          }),
          false,
          'setAnnotationUnderline'
        );
      },

      removeAnnotation: (questionId: string, id: string) => {
        set(
          (state) => ({
            annotationsByQuestionId: {
              ...state.annotationsByQuestionId,
              [questionId]: (state.annotationsByQuestionId[questionId] ?? []).filter(
                (annotation) => annotation.id !== id
              )
            }
          }),
          false,
          'removeAnnotation'
        );
      },

      setHasAnnotatableContent: (hasContent: boolean) => {
        set({hasAnnotatableContent: hasContent}, false, 'setHasAnnotatableContent');
      },

      reset: () => {
        set(initialState, false, 'reset');
      }
    }),
    {name: 'AnnotationModeStore'}
  )
);

/**
 * Hook that resets the annotation mode store on mount and unmount.
 * Call this at the practice-session level (e.g. AssignmentPracticeView), not
 * inside the per-question subtree — PracticeView remounts on every question
 * navigation and the mode must stay on across questions.
 */
export function useAnnotationModeReset() {
  const reset = useAnnotationModeStore((state) => state.reset);

  useEffect(() => {
    reset();
    return () => {
      reset();
    };
  }, [reset]);
}
